import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { ActivatedRoute } from '@angular/router';
import { SpecialtiesService } from '../specialties/specialties.service';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import moment from 'moment';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import { Device } from '@capacitor/device';
import { FirebaseService } from '@app/shared/services/firebase/firebase.service';
import { Logger } from '@app/core/logger.service';
const log = new Logger('HomeComponent');

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  scrollDepthTriggered = false;

  constructor(
    private aRoute: ActivatedRoute,
    private fireService: FirebaseService,
    private specialtiesService: SpecialtiesService,
    public fireAuth: AngularFireAuth,
  ) {
    this.setScreenName(String(this.aRoute.snapshot.url[0]));
  }

  async setScreenName(screenName: string) {
    try {
      const info = await Device.getInfo();
      if (info.platform !== 'web') {
        await FirebaseAnalytics.setScreenName({
          screenName,
        });
      }
    } catch (error) {
      log.error('Error setting screen name:', error);
    }
  }

  async initFb() {
    try {
      const info = await Device.getInfo();
      if (info.platform === 'web') {
        await FirebaseAnalytics.initializeFirebase(environment.FIREBASE_CONFIG);
      }
    } catch (error) {
      log.error('Error initializing Firebase Analytics:', error);
    }
  }

  reset() {
    try {
      FirebaseAnalytics.reset();
    } catch (error) {
      log.error('Error resetting Firebase Analytics:', error);
    }
  }

  async setUser(userIdValue: string) {
    try {
      await FirebaseAnalytics.setUserId({
        userId: userIdValue,
      });
    } catch (error) {
      log.error('Error setting user ID:', error);
    }
  }

  async setUserProperties(userIdValue: string, staff: boolean) {
    try {
      const data = await this.getUserData(userIdValue);
      if (!data) return;
      let analytics = {
        typeUser: data.type || '',
        staff: staff ? 'true' : 'false',
        user_registration_date: '',
        user_name: '',
        user_city: '',
        user_state: '',
        specialty: '',
        company: '',
      };

      const _fireAuth = await this.fireAuth.currentUser;
      if (_fireAuth && _fireAuth.metadata && _fireAuth.metadata.creationTime) {
        analytics.user_registration_date = moment(_fireAuth.metadata.creationTime).format();
      }
      analytics.user_name = `${data.name || ''} ${data.lastName1 || ''} ${data.lastName2 || ''}`.trim();

      if (data.type === 'medico') {
        const additionalData = await this.getDoctorData(userIdValue);
        if (additionalData && additionalData.address1) {
          if (additionalData.address1.city === 'Ciudad de México') {
            analytics.user_city = additionalData.address1.delegation || '';
          } else {
            analytics.user_city = additionalData.address1.city || '';
          }
          analytics.user_state = additionalData.address1.state || '';
        }

        if (additionalData && additionalData.specialty1 && additionalData.specialty1.name) {
          analytics.specialty = additionalData.specialty1.name;
        }
      } else {
        const additionalData: any = await this.fireService.afs
          .collection('representante-meta')
          .doc(userIdValue)
          .ref.get();
        if (additionalData.exists && additionalData.data() && additionalData.data().company) {
          const companyDoc = await additionalData.data().company.get();
          if (companyDoc.exists && companyDoc.data().name) {
            analytics.company = companyDoc.data().name;
          }
        }
      }

      this.setProperty('typeUser', analytics.typeUser);
      this.setProperty('staff', analytics.staff);
      this.setProperty('user_registration_date', analytics.user_registration_date);
      this.setProperty('user_name', analytics.user_name);
      this.setProperty('user_city', analytics.user_city);
      this.setProperty('user_state', analytics.user_state);
      this.setProperty('specialty', analytics.specialty);
      this.setProperty('company', analytics.company);
    } catch (error) {
      log.error('Error in setUserProperties:', error);
    }
  }

  setProperty(nameProperty: string, valueProperty: any) {
    try {
      if (valueProperty === null || valueProperty === undefined) {
        valueProperty = '';
      }
      FirebaseAnalytics.setUserProperty({
        name: nameProperty,
        value: String(valueProperty),
      });
    } catch (error) {
      log.error('Error setting user property:', error);
    }
  }

  /*
    'click'
    'advertising_banner_click'
    'chat_campaign_message_open'
    'notification_open'
  */

  async logEvent(_nameEvent: string, _paramsEvent: any = {}, additional?: any) {
    try {
      const info = await Device.getInfo();
      if (info.platform !== 'web') {
        try {
          await FirebaseAnalytics.getAppInstanceId();
        } catch (e) {
          log.error('Error getting app instance ID:', e);
        }
      }
      await this._logEvent(_nameEvent, _paramsEvent, additional);
    } catch (error) {
      log.error('Error in logEvent:', error);
    }
  }

  private sanitizeParams(params: any): any {
    const sanitized: any = {};
    if (!params) return sanitized;

    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== null && value !== undefined && value !== '') {
        // Firebase parameters must be strings, numbers or booleans
        if (typeof value === 'object') {
          sanitized[key] = JSON.stringify(value).substring(0, 100);
        } else if (typeof value === 'string') {
          sanitized[key] = value.substring(0, 100);
        } else {
          sanitized[key] = value;
        }
      }
    });
    return sanitized;
  }

  async _logEvent(_nameEvent: string, _paramsEvent: any = {}, additional?: any) {
    const cred = additional && additional.credentials ? additional.credentials : {};
    const routeInfo =
      additional && additional.page_data
        ? { app_page_route: additional.page_data.route, app_page_title: additional.page_data.title }
        : {};
    let userData: any = {
      user_company: cred && cred.company ? cred.company : undefined,
      user_specialty: cred && cred.speciality ? cred.speciality : undefined,
      user_state: cred && cred.state ? cred.state : undefined,
      user_type: cred && cred.type ? cred.type : undefined,
      user_id: cred && cred.uid ? cred.uid : undefined,
      user_uid: cred && cred.uid ? cred.uid : undefined,
    };
    if (cred && cred.type === 'medico') {
      if (!!!userData.user_specialty) {
        try {
          const resp = await this.getDoctorData(cred.uid);
          const specialityId = resp && resp.specialty1 && resp.specialty1.id ? resp.specialty1.id : undefined;
          const resp2 = await this.specialtiesService.getSpecialty(specialityId);
          const specialty = resp2 && resp2.name ? resp2.name : undefined;
          userData.user_specialty = specialty;
        } catch (error) {
          log.error(error);
        }
      }
    }
    const data: any = {
      name: _nameEvent,
      params: this.sanitizeParams({ ...userData, ..._paramsEvent, ...routeInfo }),
    };
    try {
      await FirebaseAnalytics.logEvent(data);
    } catch (error) {
      log.error('Error logging event:', error);
    }
  }

  async logScrolling($event: any, item: any, data: any) {
    if (this.scrollDepthTriggered) {
      return;
    }
    const scrollElement = await $event.target.getScrollElement();
    const scrollHeight = scrollElement.scrollHeight - scrollElement.clientHeight;
    const currentScrollDepth = $event.detail.scrollTop;
    const targetPercent = 90;
    const triggerDepth = (scrollHeight / 100) * targetPercent;
    if (currentScrollDepth > triggerDepth) {
      this.scrollDepthTriggered = true;
      this.logEvent('scroll', item, data);
    }
  }

  async getDoctorData(uid: string): Promise<any> {
    try {
      const response = await this.fireService.afs.collection('medico-meta').doc(uid).ref.get();
      const data: any = response.data();
      if (data && data.specialty1 && data.specialty1.id) {
        const specialty = await this.specialtiesService.getSpecialty(String(data.specialty1.id));
        data.specialty1.name = specialty.name;
      }
      if (data && data.specialty2 && data.specialty2.id) {
        const specialty = await this.specialtiesService.getSpecialty(String(data.specialty2.id));
        data.specialty2.name = specialty.name;
      }
      return { uid: response.id, ...data };
    } catch (error) {
      log.error(error);
      return undefined;
    }
  }

  async getUserData(uid: string): Promise<any> {
    try {
      const response = await this.fireService.afs.collection('users').doc(uid).ref.get();
      return response.data();
    } catch (error) {
      log.error(error);
      return undefined;
    }
  }
}
